import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';
import { LinearProgress, Typography } from '@material-ui/core';

import { PrimaryButton, TextField } from '../../components/AtomComponents';

import useDataService from '../../hooks/useDataService';
import apiToUrlMap, { formatString } from '../../ApiMapping';

import {
  IItem,
  IPurchaseOrder,
  ITestProvider,
  ISelectionState,
  IPxwSelectionFormProps,
  IPurchaseOrdersResponse,
} from './types';
import { eMessageType } from '../../types/IMessageType';

const INITIAL_STATE: ISelectionState = {
  testProviders: [],
  selectedTestProvider: null,
  purchaseOrders: [],
  selectedPO: null,
  itemList: [],
  selectedItem: null,
  matchQty: '',
  mismatchFunctionalQty: '',
  mismatchNonFunctionalQty: '',
  isLoading: false,
};

const PxwSelectionForm: React.FC<IPxwSelectionFormProps> = ({
  selectedTenant,
  isLoading,
  onSynthesize,
  onResultInvalidated,
}) => {
  const [form, setForm] = useState<ISelectionState>(INITIAL_STATE);

  const { fetchUrl, openSnackBar } = useDataService();

  const updateForm = (partial: Partial<ISelectionState>) =>
    setForm((prev) => ({ ...prev, ...partial }));

  useEffect(() => {
    setForm({ ...INITIAL_STATE, isLoading: !!selectedTenant });
    if (!selectedTenant) return;

    const loadPurchaseOrders = async () => {
      try {
        const result: IPurchaseOrdersResponse = await fetchUrl(
          'GET',
          formatString(apiToUrlMap.pxwDeviceTestSandboxPurchaseOrders, { tenantId: selectedTenant })
        );
        updateForm({
          testProviders: result.testProviders ?? [],
          purchaseOrders: result.purchaseOrders ?? [],
          isLoading: false,
        });
      } catch (e: any) {
        openSnackBar(e.message || 'Unable to fetch purchase orders', eMessageType.error);
        updateForm({ isLoading: false });
      }
    };
    loadPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenant]);

  const handleTestProviderChange = (_: any, value: ITestProvider | null) => {
    setForm((prev) => ({
      ...prev,
      selectedTestProvider: value,
      selectedPO: null,
      itemList: [],
      selectedItem: null,
      matchQty: '',
      mismatchFunctionalQty: '',
      mismatchNonFunctionalQty: '',
    }));
    onResultInvalidated();
  };

  const handlePOChange = (_: any, value: IPurchaseOrder | null) => {
    setForm((prev) => ({
      ...prev,
      selectedPO: value,
      itemList: value ? value.items : [],
      selectedItem: null,
      matchQty: '',
      mismatchFunctionalQty: '',
      mismatchNonFunctionalQty: '',
    }));
    onResultInvalidated();
  };

  const handleItemChange = (_: any, value: IItem | null) => {
    setForm((prev) => ({
      ...prev,
      selectedItem: value,
      matchQty: '',
      mismatchFunctionalQty: '',
      mismatchNonFunctionalQty: '',
    }));
    onResultInvalidated();
  };

  const {
    testProviders,
    selectedTestProvider,
    purchaseOrders,
    selectedPO,
    itemList,
    selectedItem,
    matchQty,
    mismatchFunctionalQty,
    mismatchNonFunctionalQty,
  } = form;

  const totalQty = useMemo(
    () =>
      (matchQty === '' ? 0 : matchQty) +
      (mismatchFunctionalQty === '' ? 0 : mismatchFunctionalQty) +
      (mismatchNonFunctionalQty === '' ? 0 : mismatchNonFunctionalQty),
    [matchQty, mismatchFunctionalQty, mismatchNonFunctionalQty]
  );

  const receivedQty = selectedItem?.quantityInProcessingSourceBins ?? -1;

  const isSubmitDisabled =
    isLoading ||
    !selectedTenant ||
    !selectedTestProvider ||
    !selectedPO ||
    !selectedItem ||
    matchQty === '' ||
    mismatchFunctionalQty === '' ||
    mismatchNonFunctionalQty === '' ||
    totalQty !== receivedQty;

  const showValidationHint =
    !!selectedItem &&
    (matchQty !== '' || mismatchFunctionalQty !== '' || mismatchNonFunctionalQty !== '');

  const handleSubmit = () => {
    onSynthesize({
      purchaseOrderNumber: selectedPO!.purchaseOrderNumber,
      itemNumber: selectedItem!.itemNumber,
      provider: selectedTestProvider!.testProviderId,
      matchCount: matchQty as number,
      mismatchFunctionalCount: mismatchFunctionalQty as number,
      mismatchNonfunctionalCount: mismatchNonFunctionalQty as number,
      selectedItem: selectedItem!,
    });
  };

  if (form.isLoading) {
    return (
      <div className="cell margin-top-1 margin-bottom-2 small-12 medium-6">
        <LinearProgress />
      </div>
    );
  }

  return (
    <>
      {/* Provider + PO + Item */}
      <div className="margin-top-1">
        <p className="text-faded">
          Select the test provider, purchase order, and sku to generate test data for.
        </p>
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell small-12 medium-4">
            <Autocomplete
              options={testProviders}
              value={selectedTestProvider}
              disabled={!selectedTenant}
              getOptionLabel={(o) => o.testProviderName}
              onChange={handleTestProviderChange}
              renderInput={(params) => (
                <TextField {...params} label="Select Test Provider" variant="outlined" />
              )}
            />
          </div>
          <div className="cell small-12 medium-4">
            <Autocomplete
              options={purchaseOrders}
              value={selectedPO}
              disabled={!selectedTestProvider}
              getOptionLabel={(o) => o.purchaseOrderNumber}
              onChange={handlePOChange}
              renderInput={(params) => (
                <TextField {...params} label="Select PO" variant="outlined" />
              )}
            />
          </div>
          <div className="cell small-12 medium-4">
            <Autocomplete
              options={itemList}
              value={selectedItem}
              disabled={!selectedPO}
              getOptionLabel={(o) => `${o.itemNumber} (Received: ${o.quantityInProcessingSourceBins})`}
              onChange={handleItemChange}
              renderInput={(params) => (
                <TextField {...params} label="Select SKU" variant="outlined" />
              )}
            />
            <Typography
              style={{ height: '16px', display: 'inline-block' }}
              variant="caption"
              color="textSecondary">
              {selectedItem ? `Received quantity: ${selectedItem.quantityInProcessingSourceBins}` : ''}
            </Typography>
          </div>
        </div>
      </div>

      {/* Quantities */}
      <div className="margin-top-1">
        <p className="text-faded">
          Enter quantities per test result category. Total must equal the item's received quantity.
        </p>
        <div className="grid-x grid-margin-x grid-margin-y">
          <div className="cell small-12 medium-4">
            <TextField
              label="Match"
              type="number"
              variant="outlined"
              fullWidth
              disabled={!selectedItem}
              value={matchQty}
              inputProps={{ min: 0 }}
              onChange={(e) =>
                updateForm({
                  matchQty: e.target.value === '' ? '' : Math.max(0, Number(e.target.value)),
                })
              }
            />
          </div>
          <div className="cell small-12 medium-4">
            <TextField
              label="Mismatch — Functional"
              type="number"
              variant="outlined"
              fullWidth
              disabled={!selectedItem}
              value={mismatchFunctionalQty}
              inputProps={{ min: 0 }}
              onChange={(e) =>
                updateForm({
                  mismatchFunctionalQty:
                    e.target.value === '' ? '' : Math.max(0, Number(e.target.value)),
                })
              }
            />
          </div>
          <div className="cell small-12 medium-4">
            <TextField
              label="Mismatch — Non-Functional"
              type="number"
              variant="outlined"
              fullWidth
              disabled={!selectedItem}
              value={mismatchNonFunctionalQty}
              inputProps={{ min: 0 }}
              onChange={(e) =>
                updateForm({
                  mismatchNonFunctionalQty:
                    e.target.value === '' ? '' : Math.max(0, Number(e.target.value)),
                })
              }
            />
          </div>
        </div>
      </div>

      {/* Validation + submit */}
      <div className="cell margin-top-1 margin-bottom-1">
        {showValidationHint && (
          <Typography
            variant="caption"
            color={totalQty === receivedQty ? 'textSecondary' : 'error'}
            className="pxw-process-devices__validation-hint">
            Total: {totalQty} / {receivedQty} received
            {totalQty < receivedQty && ' — must equal received quantity'}
            {totalQty > receivedQty && ' — exceeds received quantity'}
          </Typography>
        )}
        <PrimaryButton
          size="small"
          className="margin-top-1"
          disabled={isSubmitDisabled}
          onClick={handleSubmit}>
          Generate Test Data
        </PrimaryButton>
      </div>
    </>
  );
};

export default PxwSelectionForm;
